import {ReactNode, useContext} from 'react';

import {Grid, SxProps, Typography} from '@mui/material';

import {EmpionPaper} from '../EmpionPaper/EmpionPaper';

import {
  CommentIcon,
  HandHoldingHeartIcon,
  HandshakeIcon,
  LaptopIcon,
  LightBulbIcon,
  PuzzlePieceIcon,
  UserAstronautIcon,
  UsersIcon,
} from '../../assets/company_culture_house';
import {LanguageContext} from '../../contexts/LanguageContext/LanguageContext';
import {CultureHouseElement} from '../../features/company/culture/dashboard/types/CultureHouseTypes';
import {greenColors, orangeColors} from '../../layouts/theme/designSystem';

interface Props {
  cultureHouseElement: Omit<CultureHouseElement, 'surveyResults'>;
  onClick?: () => void;
  disabled?: boolean;
}

export const CultureHouseCard = ({cultureHouseElement, onClick, disabled}: Props) => {
  const currentLanguage = useContext(LanguageContext);
  const iconFillStyles = greenColors[700];
  const pillarIcons: {[key: string]: ReactNode} = {
    new_work_and_hybrid_work: <LaptopIcon fill={iconFillStyles} aria-label='laptop icon' />,
    interaction_and_empathy: <HandHoldingHeartIcon fill={iconFillStyles} aria-label='hand holding heart icon' />,
    development_and_communication: <CommentIcon fill={iconFillStyles} aria-label='comment icon' />,
    performance_and_organization: <PuzzlePieceIcon fill={iconFillStyles} aria-label='puzzle piece icon' />,
    leadership_and_team: <UsersIcon fill={iconFillStyles} aria-label='three users icon' />,
    purpose_and_value: <HandshakeIcon fill={iconFillStyles} aria-label='handshake icon' />,
    innovation_and_convention: <LightBulbIcon fill={iconFillStyles} aria-label='lightbulb icon' />,
    appearance_and_risk: <UserAstronautIcon fill={iconFillStyles} aria-label='austronaut icon' />,
  };
  const iconComponent = pillarIcons[cultureHouseElement.key];

  const hoverStyles = {
    '&:hover': {
      backgroundColor: orangeColors[4],
      cursor: disabled ? 'default' : 'pointer',
      '& .cultureHouseDescription': {
        color: greenColors[700],
      },
      '& svg': {
        fill: greenColors[700],
      },
    },
  };

  const paperStyles: SxProps = {
    p: 4,
    borderRadius: 4,
    minHeight: '278px',
    height: '100%',
    boxShadow: 'none',
    border: `1px solid ${orangeColors[3]}`,
    '& .cultureHouseDescription': {
      color: greenColors[700],
    },
    ...(onClick && !disabled && hoverStyles),
  };

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  return (
    <Grid item key={cultureHouseElement.resourceId} sx={{flexBasis: {xs: '100%', md: '40%'}, flexGrow: 1}}>
      <EmpionPaper sx={paperStyles} onClick={handleClick} data-testid='empion-paper'>
        {iconComponent}
        <Typography variant='body1' fontWeight={700} mb={1}>
          {cultureHouseElement.pillarTitle[currentLanguage]}
        </Typography>
        <Typography className='cultureHouseDescription' variant='body1'>
          {cultureHouseElement.pillarDescription[currentLanguage]}
        </Typography>
      </EmpionPaper>
    </Grid>
  );
};
